import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/**
 * Calendário inline em pt-BR. Não dropdown — sempre visível.
 * value: 'YYYY-MM-DD' | ''
 * onChange: (dateStrISO) => void  // continua salvando em ISO p/ backend
 * blockedDates: Set<'YYYY-MM-DD'>  -> dias do calendário que não podem ser escolhidos
 */
export const InlineDatePicker = ({ value, onChange, blockedDates }) => {
  const initial = value ? new Date(value + 'T12:00:00') : new Date();
  const [view, setView] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));

  const year = view.getFullYear();
  const month = view.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const todayStr = new Date().toISOString().slice(0, 10);

  const cells = useMemo(() => {
    const out = [];
    for (let i = 0; i < firstDay; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [firstDay, daysInMonth]);

  const prev = () => setView(new Date(year, month - 1, 1));
  const next = () => setView(new Date(year, month + 1, 1));

  const pick = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (blockedDates?.has(dateStr)) return;
    if (dateStr < todayStr) return;
    onChange(dateStr);
  };

  return (
    <div className="rounded-md border border-white/10 bg-white/[0.015] p-3 sm:p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <button type="button" onClick={prev} className="w-10 h-10 sm:w-9 sm:h-9 rounded-full border border-white/10 flex items-center justify-center text-white/55 hover:text-white hover:border-[#C9A961]/45 active:scale-95 transition-all">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-center">
          <span className="font-display text-base sm:text-lg text-white">{MONTHS[month]}</span>
          <span className="font-body text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-[#C9A961]/75 ml-2">{year}</span>
        </div>
        <button type="button" onClick={next} className="w-10 h-10 sm:w-9 sm:h-9 rounded-full border border-white/10 flex items-center justify-center text-white/55 hover:text-white hover:border-[#C9A961]/45 active:scale-95 transition-all">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="text-center py-1 text-[10px] uppercase tracking-wider text-white/30">{d}</div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} className="aspect-square" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isPast = dateStr < todayStr;
          const isBlocked = blockedDates?.has(dateStr);
          const isSelected = value === dateStr;
          const isToday = todayStr === dateStr;
          const disabled = isPast || isBlocked;

          let cls = 'aspect-square flex items-center justify-center text-[13px] sm:text-sm rounded-md transition-all duration-200 ';
          if (isSelected) cls += 'bg-gradient-to-br from-[#C9A961] to-[#8B6F47] text-[#1a1611] font-bold shadow-md scale-105';
          else if (disabled) cls += 'text-white/15 cursor-not-allowed line-through';
          else if (isToday) cls += 'text-[#C9A961] border border-[#C9A961]/40 hover:bg-[#C9A961]/10 font-semibold active:scale-95';
          else cls += 'text-white/70 hover:bg-white/[0.06] hover:text-white active:scale-95';

          return (
            <button
              key={dateStr}
              type="button"
              disabled={disabled}
              onClick={() => pick(day)}
              data-testid={`picker-day-${dateStr}`}
              className={cls}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Format ISO YYYY-MM-DD to "DD/MM/AAAA" or "—" if empty. */
export const formatDateBR = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

/** Formats to long pt-BR like "12 de fevereiro de 2026". */
export const formatDateBRLong = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

export default InlineDatePicker;
